import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  PaperGrain,
  SectionLabel,
  Polaroid,
  handwritten,
} from "../components/editorial";
import { captureEvent } from "../lib/analytics";
import { linkify } from "../lib/linkify";
import EventMap from "../lib/EventMap";
import { googleMapsUrl } from "../lib/eventLocation";
import { WHATSAPP_COMMUNITY_URL } from "../lib/appLinks";

// WHATSAPP_COMMUNITY_URL (from lib/appLinks) is surfaced on the RSVP success
// page so people arriving via a shareable event link get pulled into the
// always-on chat right after committing to a walk.

// ─── Shared event shape (kept in-file — Vite SPA; no shared types pkg) ───
interface PublicEvent {
  id: string;
  kind: string;
  title: string;
  slug: string;
  neighbourhood: string | null;
  subtitle: string | null;
  body: string | null;
  image_url: string | null;
  starts_at: string | null;
  ends_at: string | null;
  time_label: string | null;
  ends_note: string | null;
  meeting_point: string | null;
  route_note: string | null;
  location_address: string | null;
  maps_uri: string | null;
  lat: number | null;
  lng: number | null;
  host_name: string | null;
  is_free: boolean | null;
  rsvp_enabled: boolean | null;
  meetup_url: string | null;
}

// ─── Calendar helpers ────────────────────────────────────────────────────────

const EVENT_TZ = "Europe/London";

// Convert a UTC ISO string to a local ICS datetime string (no Z suffix).
// e.g. "2026-06-07T13:00:00+00:00" → "20260607T140000" when London is BST.
function toLocalIcsDate(iso: string, tz: string): string {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (type: string) =>
    parts.find((p) => p.type === type)?.value ?? "00";
  return `${get("year")}${get("month")}${get("day")}T${get("hour")}${get("minute")}${get("second")}`;
}

// DTSTAMP must stay UTC with Z.
function toUtcIcsDate(iso: string): string {
  return iso.replace(/[-:]/g, "").replace(/\.\d+Z$/, "Z");
}

function calEndIso(startsAt: string | null, endsAt: string | null): string {
  if (endsAt) return endsAt;
  if (!startsAt) return "";
  const d = new Date(startsAt);
  d.setHours(d.getHours() + 2);
  return d.toISOString();
}

function googleCalUrl(ev: PublicEvent): string {
  const endIso = calEndIso(ev.starts_at, ev.ends_at);
  // Pass local London times (no Z) + ctz so Google creates the event in
  // the correct timezone rather than interpreting times as UTC.
  const start = ev.starts_at ? toLocalIcsDate(ev.starts_at, EVENT_TZ) : "";
  const end = endIso ? toLocalIcsDate(endIso, EVENT_TZ) : "";
  const location =
    ev.location_address || ev.meeting_point || ev.neighbourhood || "";
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: ev.title,
    dates: `${start}/${end}`,
    location,
    ctz: EVENT_TZ,
    sf: "true",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function outlookCalUrl(ev: PublicEvent): string {
  const endIso = calEndIso(ev.starts_at, ev.ends_at);
  const location =
    ev.location_address || ev.meeting_point || ev.neighbourhood || "";
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: ev.title,
    startdt: ev.starts_at ?? "",
    enddt: endIso,
    location,
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

function downloadIcs(ev: PublicEvent): void {
  const endIso = calEndIso(ev.starts_at, ev.ends_at);
  // DTSTART/DTEND use TZID so Apple Calendar / iCal show the correct
  // London time regardless of the user's device timezone.
  const start = ev.starts_at ? toLocalIcsDate(ev.starts_at, EVENT_TZ) : "";
  const end = endIso ? toLocalIcsDate(endIso, EVENT_TZ) : "";
  const location =
    ev.location_address || ev.meeting_point || ev.neighbourhood || "";
  const now = toUtcIcsDate(new Date().toISOString());
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//LetsLoop//Events//EN",
    "BEGIN:VEVENT",
    `UID:${ev.slug}@events.letsloop.app`,
    `DTSTAMP:${now}`,
    `DTSTART;TZID=${EVENT_TZ}:${start}`,
    `DTEND;TZID=${EVENT_TZ}:${end}`,
    `SUMMARY:${ev.title}`,
    ...(location ? [`LOCATION:${location}`] : []),
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  const blob = new Blob([lines.join("\r\n")], {
    type: "text/calendar;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${ev.slug}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function formatDate(startsAt: string | null): string {
  if (!startsAt) return "Date coming soon";
  const d = new Date(startsAt);
  if (Number.isNaN(d.getTime())) return "Date coming soon";
  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function formatTime(startsAt: string | null, timeLabel: string | null): string {
  if (timeLabel) return timeLabel;
  if (!startsAt) return "";
  const d = new Date(startsAt);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-GB", {
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function EventRsvpPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();

  const src = (searchParams.get("src") || "web").toLowerCase();
  const source = src === "app" ? "app" : "web";

  const [fetchState, setFetchState] = useState<
    "loading" | "ready" | "notfound" | "error"
  >("loading");
  const [event, setEvent] = useState<PublicEvent | null>(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  // Raw text of the plus-ones field. Kept as a string so it can be cleared
  // (backspaced to empty) without React snapping it straight back to 0; the
  // numeric `plusOnes` below is derived from it for submission and logic.
  const [plusOnesInput, setPlusOnesInput] = useState("0");
  const plusOnes = Math.max(
    0,
    Math.min(10, Number.parseInt(plusOnesInput, 10) || 0),
  );
  const [plusOneNames, setPlusOneNames] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [submitState, setSubmitState] = useState<
    "idle" | "loading" | "done" | "error"
  >("idle");
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Mobile-first: keep the page above-the-fold tight by hiding the long-form
  // event narrative (route, address, ends-note, host, body) behind a single
  // disclosure so the RSVP form sits close to the title. Starts CLOSED — the
  // image stays visible above the fold, the rest opens on demand.
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFetchState("loading");
    fetch(`/api/events/${encodeURIComponent(slug)}`)
      .then(async (res) => {
        if (res.status === 404) {
          if (!cancelled) setFetchState("notfound");
          return;
        }
        if (!res.ok) {
          if (!cancelled) setFetchState("error");
          return;
        }
        const data = (await res.json()) as { event?: PublicEvent };
        if (cancelled) return;
        if (data.event) {
          setEvent(data.event);
          setFetchState("ready");
        } else {
          setFetchState("notfound");
        }
      })
      .catch(() => {
        if (!cancelled) setFetchState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (event) {
      document.title = `RSVP: ${event.title} | LetsLoop`;
    } else if (fetchState === "notfound") {
      document.title = "Event not found | LetsLoop";
    } else {
      document.title = "RSVP | LetsLoop Events";
    }
  }, [event, fetchState]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setSubmitState("loading");
    setSubmitError(null);
    try {
      const res = await fetch("/api/events/rsvp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slug,
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || undefined,
          plus_ones: plusOnes,
          plus_one_names: plusOnes > 0 ? plusOneNames.trim() : undefined,
          source,
          // Honeypot — real users never fill this (it's visually hidden).
          company: honeypot,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        error?: string;
      };
      if (!res.ok || !data.success) {
        setSubmitError(data.error || "Something broke. Try again in a moment.");
        setSubmitState("error");
        return;
      }
      captureEvent("event_rsvp", {
        event_slug: slug,
        event_title: event?.title ?? "",
      });
      setSubmitState("done");
    } catch {
      setSubmitError("Couldn't reach the server. Check your connection.");
      setSubmitState("error");
    }
  };

  const backToEvents = "/";

  return (
    <>
      {/* Slim brand header — the RSVP page is a focused form, not a
          sub-page of the marketing site, so we drop the full Navbar. */}
      <header
        className="fixed top-0 inset-x-0 z-30 backdrop-blur-sm"
        style={{ backgroundColor: "rgba(250, 246, 240, 0.85)" }}
      >
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <a
            href="https://letsloop.app"
            aria-label="LetsLoop home"
            className="hover:opacity-80 transition-opacity"
          >
            <img
              src="/letsloop-logo.png"
              alt="LetsLoop"
              width={120}
              height={27}
              className="h-7 md:h-8 w-auto"
            />
          </a>
          <a
            href={backToEvents}
            className="text-xs font-semibold uppercase tracking-widest text-gray-500 hover:text-ink transition-colors"
          >
            All events →
          </a>
        </div>
      </header>
      <main
        className="min-h-screen text-ink"
        style={{ backgroundColor: "var(--color-paper)" }}
      >
        <PaperGrain />

        <section className="relative pt-16 md:pt-24 pb-12 md:pb-28 px-4 md:px-6">
          <div className="max-w-3xl mx-auto relative">
            {/* ─── LOADING ─────────────────────────────────────────── */}
            {fetchState === "loading" && (
              <div className="animate-pulse">
                <div className="h-5 w-32 bg-ink/10 rounded mb-6" />
                <div className="h-12 w-3/4 bg-ink/10 rounded mb-4" />
                <div className="h-12 w-1/2 bg-ink/10 rounded mb-10" />
                <div className="bg-canvas border-2 border-ink/10 rounded-3xl p-8 md:p-10">
                  <div className="h-5 w-40 bg-ink/10 rounded mb-4" />
                  <div className="h-5 w-2/3 bg-ink/10 rounded mb-3" />
                  <div className="h-5 w-1/2 bg-ink/10 rounded" />
                </div>
              </div>
            )}

            {/* ─── NOT FOUND ───────────────────────────────────────── */}
            {fetchState === "notfound" && (
              <div className="text-center py-16">
                <p className="text-2xl text-salmon mb-4" style={handwritten}>
                  hmm…
                </p>
                <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight mb-6">
                  That event isn&apos;t here.
                </h1>
                <p className="text-lg text-gray-600 leading-relaxed max-w-md mx-auto mb-10">
                  The link might be old, or the event may have wrapped up. Check
                  what&apos;s coming up next.
                </p>
                <a
                  href={backToEvents}
                  className="inline-block bg-ink text-canvas font-semibold py-3 px-6 rounded-xl hover:bg-salmon hover:text-ink transition-colors"
                >
                  See what&apos;s coming up →
                </a>
              </div>
            )}

            {/* ─── ERROR ───────────────────────────────────────────── */}
            {fetchState === "error" && (
              <div className="text-center py-16">
                <p className="text-2xl text-salmon mb-4" style={handwritten}>
                  one sec…
                </p>
                <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight mb-6">
                  Couldn&apos;t load this event.
                </h1>
                <p className="text-lg text-gray-600 leading-relaxed max-w-md mx-auto mb-10">
                  Something on our end hiccuped. Try again, or head back to the
                  events page.
                </p>
                <a
                  href={backToEvents}
                  className="inline-block bg-ink text-canvas font-semibold py-3 px-6 rounded-xl hover:bg-salmon hover:text-ink transition-colors"
                >
                  ← Back to events
                </a>
              </div>
            )}

            {/* ─── READY ───────────────────────────────────────────── */}
            {fetchState === "ready" && event && (
              <>
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-base md:text-3xl text-salmon mb-2 md:mb-4"
                  style={handwritten}
                >
                  → {event.neighbourhood || "London"} calling
                </motion.p>

                {event.subtitle && (
                  <p className="hidden md:block text-base md:text-xl text-gray-600 leading-relaxed mb-8 md:mb-10 max-w-2xl">
                    {event.subtitle}
                  </p>
                )}

                {/* Event summary card — mobile-first: only the date/time and
                    meeting point stay above the fold. Everything else
                    (image, route, address, ends note, host, body) is folded
                    into a single "Show event details" disclosure so the
                    RSVP form sits within thumb-reach on phones. */}
                <div className="bg-canvas border border-ink/15 rounded-3xl p-5 md:p-10 shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)] md:shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)] relative overflow-hidden mb-6 md:mb-10">
                  <div className="absolute -top-12 -right-12 w-32 h-32 bg-salmon rounded-full opacity-20" />
                  <div className="relative">
                    {/* Image stays above the fold even while the rest of the
                        event narrative is folded away — people want to see
                        where they're walking before they read the details. */}
                    {event.image_url && (
                      <div className="rounded-2xl overflow-hidden mb-5 md:mb-6 aspect-[40/21]">
                        <img
                          src={event.image_url}
                          alt={event.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    )}

                    <h2 className="font-serif text-2xl md:text-4xl tracking-tight leading-tight mb-4 md:mb-6">
                      {formatDate(event.starts_at)}
                      {formatTime(event.starts_at, event.time_label) && (
                        <>
                          <br />
                          <span className="italic">
                            at {formatTime(event.starts_at, event.time_label)}
                          </span>
                        </>
                      )}
                    </h2>

                    {event.meeting_point && (
                      <div className="mb-4 md:mb-5">
                        <dt className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">
                          Meet at
                        </dt>
                        <dd className="text-base md:text-lg">
                          {linkify(event.meeting_point)}
                        </dd>
                        {googleMapsUrl(event) && (
                          <a
                            href={googleMapsUrl(event) as string}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() =>
                              captureEvent("directions_click", {
                                event_slug: slug ?? "",
                                event_title: event.title,
                                surface: "meet_at_link",
                              })
                            }
                            className="inline-flex items-center gap-1.5 mt-2 text-sm md:text-base font-semibold text-ink border-b border-ink/30 hover:border-salmon hover:text-salmon transition-colors"
                          >
                            <span aria-hidden="true">📍</span>
                            Open in Google Maps
                            <span aria-hidden="true">→</span>
                          </a>
                        )}
                      </div>
                    )}

                    {(event.route_note ||
                      event.location_address ||
                      event.ends_note ||
                      event.host_name ||
                      event.body ||
                      event.subtitle) && (
                      <>
                        <button
                          type="button"
                          onClick={() => setDetailsOpen((v) => !v)}
                          aria-expanded={detailsOpen}
                          aria-controls="event-details-panel"
                          className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-ink/70 hover:text-ink transition-colors mt-1"
                        >
                          {detailsOpen
                            ? "Hide event details"
                            : "Show event details"}
                          <span
                            aria-hidden="true"
                            className={`inline-block transition-transform duration-200 ${
                              detailsOpen ? "rotate-180" : ""
                            }`}
                          >
                            ▾
                          </span>
                        </button>

                        <AnimatePresence initial={false}>
                          {detailsOpen && (
                            <motion.div
                              id="event-details-panel"
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.28 }}
                              className="overflow-hidden"
                            >
                              <div className="pt-5 md:pt-6">
                                {event.subtitle && (
                                  <p className="md:hidden text-base text-gray-600 leading-relaxed mb-5">
                                    {event.subtitle}
                                  </p>
                                )}

                                <dl className="space-y-4">
                                  {event.route_note && (
                                    <div>
                                      <dt className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">
                                        The route
                                      </dt>
                                      <dd className="text-base md:text-lg leading-relaxed">
                                        {linkify(event.route_note)}
                                      </dd>
                                    </div>
                                  )}
                                  {event.location_address && (
                                    <div>
                                      <dt className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">
                                        Where
                                      </dt>
                                      <dd className="text-base md:text-lg leading-relaxed">
                                        {linkify(event.location_address)}
                                      </dd>
                                    </div>
                                  )}
                                  {event.ends_note && (
                                    <div>
                                      <dt className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">
                                        Ends around
                                      </dt>
                                      <dd className="text-base md:text-lg">
                                        {event.ends_note}
                                      </dd>
                                    </div>
                                  )}
                                  {event.host_name && (
                                    <div>
                                      <dt className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">
                                        Hosted by
                                      </dt>
                                      <dd className="text-base md:text-lg">
                                        {event.host_name}
                                      </dd>
                                    </div>
                                  )}
                                  {event.meetup_url && (
                                    <div>
                                      <dt className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">
                                        Also on
                                      </dt>
                                      <dd className="text-base md:text-lg">
                                        <a
                                          href={event.meetup_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-ink font-semibold border-b border-ink/30 hover:border-salmon hover:text-salmon transition-colors"
                                        >
                                          Meetup →
                                        </a>
                                      </dd>
                                    </div>
                                  )}
                                </dl>

                                <EventMap event={event} />

                                {event.body && (
                                  <p className="text-base md:text-lg text-gray-600 leading-relaxed mt-5 whitespace-pre-line">
                                    {linkify(event.body)}
                                  </p>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    )}
                  </div>
                </div>

                {/* RSVP form / success */}
                {submitState === "done" ? (
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-canvas border border-ink/15 rounded-3xl p-6 md:p-12 text-center shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)] md:shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)]"
                  >
                    <p
                      className="text-2xl md:text-4xl text-salmon mb-3"
                      style={handwritten}
                    >
                      you&apos;re in…
                    </p>
                    <h2 className="font-serif text-2xl md:text-4xl tracking-tight leading-tight mb-6 md:mb-8">
                      See you {formatDate(event.starts_at)}.
                    </h2>

                    {/* WhatsApp community step — sits IMMEDIATELY after the
                        "you're in" so it's impossible to miss. Big salmon
                        button, kicker, mini step indicator. Email
                        confirmation is intentionally pushed below. */}
                    <div
                      className="rounded-2xl border border-ink/15 p-5 md:p-7 mb-6 text-left"
                      style={{ backgroundColor: "var(--color-paper-salmon)" }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-ink text-canvas text-xs font-bold">
                          2
                        </span>
                        <span className="text-xs font-bold uppercase tracking-widest text-ink">
                          Final step: join the chat
                        </span>
                      </div>
                      <h3 className="font-serif text-2xl md:text-3xl tracking-tight leading-tight mb-2 mt-1">
                        Join the WhatsApp community.
                      </h3>
                      <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-5">
                        Meet-up reminders, weather calls, last-minute changes
                        and chatter between events all happen here. Without it
                        you might miss a change on the day.
                      </p>
                      <a
                        href={WHATSAPP_COMMUNITY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 w-full bg-salmon text-on-salmon font-bold py-4 px-5 rounded-xl border border-ink/15 hover:bg-ink hover:text-canvas active:translate-y-0.5 transition-all text-base md:text-lg shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)] hover:shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)]"
                      >
                        <svg
                          aria-hidden="true"
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.413c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.55 5.27l.241.383-1.001 3.654 3.748-.982.011-.024zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.296-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
                        </svg>
                        Join the WhatsApp group
                      </a>
                      <p className="text-xs text-ink/70 mt-3 text-center font-medium">
                        Opens in WhatsApp · leave anytime
                      </p>
                    </div>

                    {/* Add to calendar */}
                    <div className="mb-6">
                      <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">
                        Add to your calendar
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <a
                          href={googleCalUrl(event)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-ink/20 bg-canvas text-sm font-semibold hover:border-ink hover:bg-ink hover:text-canvas transition-all"
                        >
                          Google Calendar
                        </a>
                        <a
                          href={outlookCalUrl(event)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-ink/20 bg-canvas text-sm font-semibold hover:border-ink hover:bg-ink hover:text-canvas transition-all"
                        >
                          Outlook
                        </a>
                        <button
                          type="button"
                          onClick={() => downloadIcs(event)}
                          className="flex-1 inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-ink/20 bg-canvas text-sm font-semibold hover:border-ink hover:bg-ink hover:text-canvas transition-all"
                        >
                          iCal / Apple
                        </button>
                      </div>
                    </div>

                    <p className="text-sm text-gray-500 leading-relaxed max-w-md mx-auto mb-6">
                      Confirmation sent to{" "}
                      <span className="font-semibold text-ink">{email}</span>.
                      No ticket, just turn up. Reply to that email if plans
                      change.
                    </p>

                    <a
                      href={backToEvents}
                      className="inline-block text-xs font-semibold uppercase tracking-widest text-gray-500 hover:text-ink transition-colors"
                    >
                      ← Back to all events
                    </a>
                  </motion.div>
                ) : event.rsvp_enabled === false ? (
                  <div
                    className="rounded-3xl p-8 border-2 border-ink/10 text-center"
                    style={{ backgroundColor: "var(--color-paper-salmon)" }}
                  >
                    <p className="text-2xl text-ink mb-2" style={handwritten}>
                      RSVP not open yet
                    </p>
                    <p className="text-base text-gray-600 leading-relaxed">
                      RSVP for this event isn&apos;t open. Keep an eye on{" "}
                      <a
                        href={backToEvents}
                        className="text-ink font-semibold border-b border-ink hover:text-salmon hover:border-salmon transition-colors"
                      >
                        the events page
                      </a>
                      . It&apos;ll go live soon.
                    </p>
                  </div>
                ) : (
                  <div>
                    <SectionLabel kicker="Loop in" title="Save your spot" />
                    <p
                      className="text-base md:text-xl text-gray-600 -mt-2 md:mt-0 mb-5 md:mb-8"
                      style={handwritten}
                    >
                      Free. Solo or +1. We just like to know roughly how many.
                    </p>

                    <form onSubmit={submit} className="space-y-5">
                      {/* Honeypot — visually hidden, off-screen, not tab-focusable */}
                      <div
                        aria-hidden="true"
                        style={{
                          position: "absolute",
                          left: "-9999px",
                          width: "1px",
                          height: "1px",
                          overflow: "hidden",
                        }}
                      >
                        <label htmlFor="company">
                          Company (leave this empty)
                        </label>
                        <input
                          id="company"
                          name="company"
                          type="text"
                          tabIndex={-1}
                          autoComplete="off"
                          value={honeypot}
                          onChange={(e) => setHoneypot(e.target.value)}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="rsvp-name"
                          className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2"
                        >
                          Your name
                        </label>
                        <input
                          id="rsvp-name"
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Alex Rivera"
                          required
                          maxLength={120}
                          className="w-full px-4 py-3 rounded-xl border-2 border-ink/15 bg-canvas focus:outline-none focus:border-ink/40 transition-colors text-base"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="rsvp-email"
                          className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2"
                        >
                          Email
                        </label>
                        <input
                          id="rsvp-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@hackney.com"
                          required
                          maxLength={200}
                          className="w-full px-4 py-3 rounded-xl border-2 border-ink/15 bg-canvas focus:outline-none focus:border-ink/40 transition-colors text-base"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="rsvp-phone"
                          className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2"
                        >
                          Phone{" "}
                          <span className="text-gray-400 normal-case font-normal tracking-normal">
                            (optional)
                          </span>
                        </label>
                        <input
                          id="rsvp-phone"
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="+44 7…"
                          maxLength={40}
                          className="w-full px-4 py-3 rounded-xl border-2 border-ink/15 bg-canvas focus:outline-none focus:border-ink/40 transition-colors text-base"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="rsvp-plusones"
                          className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2"
                        >
                          Bringing anyone?
                        </label>
                        <input
                          id="rsvp-plusones"
                          type="number"
                          min={0}
                          max={10}
                          value={plusOnesInput}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === "") {
                              // Allow the field to be emptied while typing.
                              setPlusOnesInput("");
                              return;
                            }
                            const n = Number.parseInt(raw, 10);
                            if (Number.isNaN(n)) return;
                            setPlusOnesInput(
                              String(Math.max(0, Math.min(10, n))),
                            );
                          }}
                          onBlur={() => {
                            // Empty field falls back to 0 once focus leaves.
                            if (plusOnesInput === "") setPlusOnesInput("0");
                          }}
                          className="w-full px-4 py-3 rounded-xl border-2 border-ink/15 bg-canvas focus:outline-none focus:border-ink/40 transition-colors text-base"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          0–10 plus-ones. Most people come solo. That&apos;s the
                          point.
                        </p>
                      </div>

                      {plusOnes > 0 && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          transition={{ duration: 0.25 }}
                        >
                          <label
                            htmlFor="rsvp-plusone-names"
                            className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2"
                          >
                            Who&apos;s coming with you?
                          </label>
                          <textarea
                            id="rsvp-plusone-names"
                            value={plusOneNames}
                            onChange={(e) => setPlusOneNames(e.target.value)}
                            placeholder="First names are fine, one per line"
                            rows={3}
                            maxLength={1000}
                            className="w-full px-4 py-3 rounded-xl border-2 border-ink/15 bg-canvas focus:outline-none focus:border-ink/40 transition-colors text-base resize-y"
                          />
                        </motion.div>
                      )}

                      <button
                        type="submit"
                        disabled={submitState === "loading"}
                        className="w-full bg-salmon text-on-salmon font-semibold py-4 px-5 rounded-xl border border-ink/15 hover:bg-ink hover:text-canvas transition-colors text-base disabled:opacity-50"
                      >
                        {submitState === "loading"
                          ? "Saving your spot…"
                          : "Save my spot →"}
                      </button>

                      {event.meetup_url && (
                        <p className="text-center text-sm text-gray-500">
                          or{" "}
                          <a
                            href={event.meetup_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-ink font-semibold border-b border-ink/30 hover:border-salmon hover:text-salmon transition-colors"
                          >
                            RSVP on Meetup →
                          </a>
                        </p>
                      )}

                      {submitState === "error" && (
                        <p className="text-sm text-red-600 text-center">
                          {submitError ||
                            "Something broke. Try again or DM us on Instagram."}
                        </p>
                      )}

                      <p className="text-xs text-gray-400 text-center leading-relaxed pt-2">
                        We email you a confirmation and nothing else unless you
                        ask. No spam, ever. See our{" "}
                        <a
                          href="https://letsloop.app/privacy"
                          className="text-gray-500 underline hover:text-ink"
                        >
                          privacy policy
                        </a>
                        .
                      </p>
                    </form>
                  </div>
                )}

                {/* Sign-off polaroid flourish */}
                <div className="mt-20 flex justify-center">
                  <div className="-rotate-2 transform-gpu">
                    <Polaroid
                      src="/polaroid-sunday.webp"
                      caption="see you there"
                      size="sm"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      </main>
    </>
  );
}
